import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  NoSymbolIcon, TrashIcon,
  PencilSquareIcon, CheckIcon, XMarkIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { waybillAPI, uomAPI } from '../../api/waybillAPI';

function WaybillGLBadge({ status, error, waybillId, onRetry, onUpdate, loading }) {
  if (status === 'posted') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">GL Posted</span>;
  }
  if (status === 'failed') {
    return (
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" title={error || 'GL posting failed'}>
          GL Failed
        </span>
        <button
          onClick={() => onRetry(waybillId)}
          disabled={loading}
          className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? 'Posting…' : 'Retry'}
        </button>
      </div>
    );
  }
  // pending — first-time post not yet attempted
  return (
    <div className="flex items-center gap-1.5">
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        GL Pending
      </span>
      <button
        onClick={() => onRetry(waybillId)}
        disabled={loading}
        className="text-xs text-teal-600 hover:underline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? 'Posting…' : 'Post to GL'}
      </button>
    </div>
  );
}
import CustomerSearch from '../../components/CustomerSearch';
import DriverSearch from '../../components/DriverSearch';
import AuditTimeline from '../../components/billing/AuditTimeline';
import useGuardedMutation from '../../hooks/useGuardedMutation';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().split('T')[0];

const emptyForm = { 
  SerialNo: '',
  TransactionDate: today(),
  Shipper: '',
  ShipperAddress: '',
  CustomerId: '',
  CustomerAddress: '',
  Origin: '',
  Destination: '',
  Vessel: '',
  Voyage: '',
  PlateNo: '',
  VanNo: '',
  VanSize: '',
  Description: '',
  SealNo: '',
  HasWithHolding: false,
  StrippingFee: 0,
  AdditionalStrippingFee: 0,
  IsDelivered: false,
  referenceNumber: '',
  notes: '',
  customerName: '',
  ShipmentType: 'trucking',
  driver: null,        // ObjectId
  driverObj: null,     // populated object for DriverSearch display
  customerObj: null,   // populated object for CustomerSearch display
};

function computeTruckingSubTotal(f) {
  const qty = parseFloat(f.quantity  || 0);
  const fr  = parseFloat(f.freight   || 0);
  const adj = parseFloat(f.adjustments || 0);
  return qty * fr + adj;
}

function resolveUOM(line, uomOptions) {
  const raw = line.unitOfMeasureId || line.UnitOfMeasureId;
  // Case 1: populated object
  if (raw && typeof raw === 'object') {
    return raw.Abbreviation || raw.UnitOfMeasureName || raw.abbreviation || raw.unitOfMeasureName || '—';
  }
  // Case 2: plain string ID
  if (raw && typeof raw === 'string') {
    const match = uomOptions.find((u) => u._id === raw);
    return match?.Abbreviation || match?.UnitOfMeasureName || match?.abbreviation || '—';
  }
  return '—';
}

export default function TruckingWaybillEditPage() {
  const { id } = useParams();
  const isAdd = !id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [uomOptions, setUomOptions] = useState([]);
  const [lines, setLines] = useState([]);
  const [editingId, setEditingId] = useState(null);   // _id | 'new' | null
  const [editForm, setEditForm] = useState({});
  const [lineSaving, setLineSaving] = useState(false);
  const [loading, setLoading] = useState(!isAdd);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isVoided, setIsVoided] = useState(false);
  const [voidedInfo, setVoidedInfo] = useState({ reason: '', at: null });
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFetched, setAuditFetched] = useState(false);
  const [glPostingStatus, setGlPostingStatus] = useState('pending');
  const [glPostingError, setGlPostingError] = useState(null);
  const [glPosting, setGlPosting] = useState(false);
  const [deletingLineId, setDeletingLineId] = useState(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'audit' && !auditFetched && id) {
      setAuditLoading(true);
      waybillAPI.getAuditLog(id)
        .then((res) => {
          setAuditLog(Array.isArray(res.data?.data) ? res.data.data : []);
          setAuditFetched(true);
        })
        .catch(() => {})
        .finally(() => setAuditLoading(false));
    }
  };

  const handleRetryGL = async (waybillId) => {
    if (glPosting) return;
    setGlPosting(true);
    try {
      await waybillAPI.retryWaybillGLPost(waybillId);
      setGlPostingStatus('posted');
      setGlPostingError(null);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'GL retry failed';
      setGlPostingError(msg);
      setGlPostingStatus('failed');
    } finally {
      setGlPosting(false);
    }
  };

  useEffect(() => {
    uomAPI.getAll().then((r) => setUomOptions(r.data?.data || [])).catch(() => {});

    if (isAdd) {
      waybillAPI.nextSerial().then((r) => setForm((p) => ({ ...p, SerialNo: r.data.serial }))).catch(() => {});
    }

    if (!isAdd) {
      waybillAPI.getById(id)
        .then((r) => {
          const wb = r.data;
          setForm({
            SerialNo:         wb.SerialNo         || '',
            TransactionDate:  wb.TransactionDate ? wb.TransactionDate.split('T')[0] : today(),
            Shipper:          wb.Shipper          || '',
            ShipperAddress:   wb.ShipperAddress   || '',
            CustomerId:       wb.CustomerId?._id  || wb.CustomerId || '',
            customerName:     wb.CustomerId?.CustomerName || wb.CustomerId?.name || '',
            customerObj:      wb.CustomerId && typeof wb.CustomerId === 'object'
              ? { ...wb.CustomerId, name: wb.CustomerId.CustomerName || wb.CustomerId.name || '' }
              : null,
            CustomerAddress:  wb.CustomerAddress  || '',
            Origin:           wb.Origin           || '',
            Destination:      wb.Destination      || '',
            Vessel:           wb.Vessel           || '',
            Voyage:           wb.Voyage           || '',
            PlateNo:          wb.PlateNo          || '',
            VanNo:            wb.VanNo            || '',
            VanSize:          wb.VanSize          ?? '',
            Description:      wb.Description      || '',
            SealNo:           wb.SealNo           || '',
            HasWithHolding:          !!wb.HasWithHolding,
            StrippingFee:            wb.StrippingFee            || 0,
            AdditionalStrippingFee:  wb.AdditionalStrippingFee  || 0,
            IsDelivered:             !!wb.IsDelivered,
            referenceNumber:  wb.referenceNumber  || '',
            notes:            wb.notes            || '',
            ShipmentType:     'trucking',
            driver:           wb.driver?._id      || wb.driver || null,
            driverObj:        wb.driver && typeof wb.driver === 'object' ? wb.driver : null,
          });
          setIsVoided(!!wb.isVoided);
          setVoidedInfo({ reason: wb.voidReason || '', at: wb.voidedAt || null });
          setGlPostingStatus(wb.glPostingStatus || 'pending');
          setGlPostingError(wb.glPostingError || null);
          setLoading(false);
        })
        .catch(() => { setError('Failed to load waybill.'); setLoading(false); });

      waybillAPI.getLines(id)
        .then((r) => setLines(Array.isArray(r.data?.data) ? r.data.data : []))
        .catch(() => {});
    }
  }, [id, isAdd]);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [field]: val }));
  };

  const { mutate: handleSave, isPending: saving } = useGuardedMutation(async () => {
    if (isVoided) return;
    setError(''); setSuccess(false);
    try {
      const { driverObj, customerObj, ...rest } = form;
      const payload = { ...rest, shipmentType: 'trucking' };
      if (isAdd) {
        const r = await waybillAPI.create(payload);
        navigate(`/billing/trucking-waybill/${r.data._id}/edit`);
      } else {
        await waybillAPI.update(id, payload);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (e) {
      const d = e?.response?.data;
      setError(d?.detail ? `${d.error}: ${d.detail}` : (d?.error || 'Failed to save waybill.'));
      throw e; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handlePrint = async () => {
    try {
      const res = await waybillAPI.getPrintToken(id);
      const data = res.data;
      if (data.success) {
        window.open(`/billing/reports/trucking-waybill/${data.printToken}`, '_blank');
      }
    } catch (e) {
      console.error('Failed to generate print token', e);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true);
    try {
      await waybillAPI.voidWaybill(id, voidReason);
      setIsVoided(true);
      setVoidedInfo({ reason: voidReason, at: new Date().toISOString() });
      setShowVoidModal(false);
      setVoidReason('');
      setAuditFetched(false);
      setAuditLog([]);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to void waybill.');
    } finally {
      setVoiding(false);
    }
  };

  // ── Line helpers ───────────────────────────────────────────────
  function startEdit(line) {
    const uomRaw = line.unitOfMeasureId || line.UnitOfMeasureId;
    const uomId  = uomRaw && typeof uomRaw === 'object'
      ? (uomRaw._id?.toString() || '')
      : (uomRaw || '');
    setEditingId(line._id);
    setEditForm({
      description:     line.Description || line.description || '',
      unitOfMeasureId: uomId,
      quantity:        String(line.Quantity  ?? line.quantity  ?? ''),
      freight:         String(line.Freight   ?? line.freight   ?? ''),
      adjustments:     String(line.Adjustments ?? line.adjustments ?? 0),
    });
  }

  function handleLineChange(field, value) {
    setEditForm((p) => ({ ...p, [field]: value }));
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  const handleSaveLine = async () => {
    if (lineSaving) return;
    setLineSaving(true);
    const isNew = editingId === 'new';
    const payload = {
      description:     editForm.description,
      unitOfMeasureId: editForm.unitOfMeasureId,
      quantity:        parseFloat(editForm.quantity    || 0),
      freight:         parseFloat(editForm.freight     || 0),
      adjustments:     parseFloat(editForm.adjustments || 0),
      subTotal:        computeTruckingSubTotal(editForm),
      billingType:     'per_pc',
      width: 0, height: 0, length: 0, weight: 0,
    };
    try {
      let r;
      if (isNew) {
        r = await waybillAPI.addLine(id, payload);
      } else {
        r = await waybillAPI.updateLine(id, editingId, payload);
      }
      if (r.data?.waybillGL) {
        setGlPostingStatus(r.data.waybillGL.glPostingStatus || 'pending');
        setGlPostingError(r.data.waybillGL.glPostingError || null);
      }
      const lines = await waybillAPI.getLines(id);
      setLines(Array.isArray(lines.data?.data) ? lines.data.data : []);
      handleCancelEdit();
    } catch (e) {
      setError(isNew ? 'Failed to add line.' : 'Failed to update line.');
    } finally {
      setLineSaving(false);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (deletingLineId || !window.confirm('Delete this line?')) return;
    setDeletingLineId(lineId);
    try {
      const r = await waybillAPI.deleteLine(id, lineId);
      setLines((prev) => prev.filter((l) => l._id !== lineId));
      if (r.data?.waybillGL) {
        setGlPostingStatus(r.data.waybillGL.glPostingStatus || 'pending');
        setGlPostingError(r.data.waybillGL.glPostingError || null);
      }
    } catch (e) {
      setError('Failed to delete line.');
    } finally {
      setDeletingLineId(null);
    }
  };

  // ── Computed totals ────────────────────────────────────────────
  const sumSubTotal    = lines.reduce((s, l) => s + (l.SubTotal ?? l.subTotal ?? 0), 0);
  const totalAmount    = sumSubTotal;
  const netOfVat       = totalAmount / 1.12;
  const vat            = netOfVat * 0.12;
  const withholding    = form.HasWithHolding ? netOfVat * 0.02 : 0;
  const stripFee       = parseFloat(form.StrippingFee) || 0;
  const addlStripFee   = parseFloat(form.AdditionalStrippingFee) || 0;
  const amountDue      = form.HasWithHolding
    ? totalAmount + stripFee + addlStripFee - withholding
    : totalAmount + stripFee + addlStripFee;

  if (loading) {
    return (
      <Layout pageTitle="Trucking Waybill">
        <div className="flex items-center justify-center h-48 text-gray-400">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle={isAdd ? 'New Trucking Waybill' : `Trucking Waybill — ${form.SerialNo}`}>
      <div className="space-y-6 max-w-5xl">

        {/* ── Tabs ─────────────────────────────────────────────── */}
        {!isAdd && (
          <div className="border-b border-gray-200">
            <nav className="flex gap-0" aria-label="Tabs">
              {[{ key: 'details', label: 'Details' }, { key: 'audit', label: 'Audit Trail' }].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-teal-600 text-teal-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* ── Audit Trail tab ───────────────────────────────────── */}
        {!isAdd && activeTab === 'audit' && (
          <div className="space-y-6">
            {auditLoading && <div className="text-center text-gray-400 py-12 text-sm">Loading audit trail…</div>}
            {!auditLoading && auditLog.length === 0 && <div className="text-center text-gray-400 py-12 text-sm">No audit records found.</div>}
            {!auditLoading && auditLog.length > 0 && <AuditTimeline entries={auditLog} />}
          </div>
        )}

        {/* ── Details tab ───────────────────────────────────────── */}
        {(isAdd || activeTab === 'details') && (
          <>
            {/* Voided banner */}
            {!isAdd && isVoided && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm">
                <NoSymbolIcon className="h-5 w-5 shrink-0" />
                <div>
                  <span className="font-semibold">This waybill has been voided.</span>
                  {voidedInfo.reason && <span className="ml-2">Reason: {voidedInfo.reason}</span>}
                  {voidedInfo.at && (
                    <span className="ml-2 text-red-500">
                      {new Date(voidedInfo.at).toLocaleDateString('en-PH', { dateStyle: 'medium' })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm font-medium">
                Waybill saved successfully.
              </div>
            )}

            {/* ── Header Fields ─────────────────────────────────── */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">Waybill Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <F label="Ref No."      value={form.SerialNo}         onChange={set('SerialNo')} />
                <F label="Waybill No."  value={form.referenceNumber}  onChange={set('referenceNumber')} />
                <F label="Date"         type="date" value={form.TransactionDate} onChange={set('TransactionDate')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <F label="Shipper"          value={form.Shipper}          onChange={set('Shipper')} />
                <F label="Shipper Address"  value={form.ShipperAddress}   onChange={set('ShipperAddress')} />

                {/* Consignee / Customer search */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Consignee</label>
                  <CustomerSearch
                    value={form.customerObj}
                    onSelect={(c) => {
                      if (!c) {
                        setForm((p) => ({ ...p, CustomerId: '', customerName: '', customerObj: null }));
                        return;
                      }
                      const name = c.CustomerName || c.name || '';
                      const fullAddress = [c.address || c.Address, c.city || c.City]
                        .filter(Boolean)
                        .join(', ');
                      setForm((p) => ({
                        ...p,
                        CustomerId:      c._id || '',
                        customerName:    name,
                        customerObj:     { ...c, name },
                        CustomerAddress: p.CustomerAddress?.trim()
                          ? p.CustomerAddress
                          : fullAddress,
                      }));
                    }}
                  />
                </div>
                <F label="Consignee Address" value={form.CustomerAddress} onChange={set('CustomerAddress')} />

                {/* Driver — typeahead search */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Driver</label>
                  <DriverSearch
                    value={form.driverObj}
                    onChange={(d) =>
                      setForm((p) => ({
                        ...p,
                        driver:      d?._id || null,
                        driverObj:   d || null,
                        PlateNo:     d?.truckPlateCode || p.PlateNo,
                        VanSize:     d?.vanSize || p.VanSize,
                        Description: d?.description || p.Description,
                      }))
                    }
                    disabled={isVoided}
                  />
                </div>
                <div /> {/* grid spacer */}

                <F label="Origin"      value={form.Origin}      onChange={set('Origin')} />
                <F label="Destination" value={form.Destination} onChange={set('Destination')} />
                <F label="Vessel No."  value={form.Vessel}      onChange={set('Vessel')} />
                <F label="Voyage"      value={form.Voyage}      onChange={set('Voyage')} />
                <F label="Plate No."   value={form.PlateNo}     onChange={set('PlateNo')} />
                <F label="Van No."     value={form.VanNo}       onChange={set('VanNo')} />
                <F label="Van Size"    value={form.VanSize}     onChange={set('VanSize')} />
                <F label="Seal No."    value={form.SealNo || ''} onChange={set('SealNo')} />
                <F label="Description" value={form.Description} onChange={set('Description')} />

                <div className="flex items-end gap-4">
                  <CB label="Has Withholding" checked={form.HasWithHolding} onChange={set('HasWithHolding')} />
                </div>
                <NumericF label="Stripping Fee (₱)" value={form.StrippingFee} onChange={set('StrippingFee')} />
                <NumericF label="Add'l Stripping Fee (₱)" value={form.AdditionalStrippingFee} onChange={set('AdditionalStrippingFee')} />

                <div className="flex items-end gap-4">
                  <CB label="Is Delivered" checked={form.IsDelivered} onChange={set('IsDelivered')} />
                </div>

                <div className="col-span-full">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={set('notes')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* ── Line Items ────────────────────────────────────── */}
            {isAdd && (
              <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-400 text-center">
                Save the waybill header first to add line items.
              </div>
            )}
            {!isAdd && (
              <div className="bg-white rounded-lg shadow p-6 space-y-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Freight Lines</h2>

                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">UOM</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Freight</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Adjustment</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Sub Total</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.length === 0 && editingId !== 'new' ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-gray-400">No line items yet.</td>
                      </tr>
                    ) : (
                      lines.map((l) =>
                        editingId === l._id ? (
                          // ── Edit row ───────────────────────────────
                          <tr key={l._id} className="bg-teal-50 ring-1 ring-teal-200 ring-inset">
                            <td className="px-1 py-1">
                              <input type="text" value={editForm.description || ''}
                                onChange={(e) => handleLineChange('description', e.target.value)}
                                className={editInputCls} />
                            </td>
                            <td className="px-1 py-1">
                              <select value={editForm.unitOfMeasureId || ''}
                                onChange={(e) => handleLineChange('unitOfMeasureId', e.target.value)}
                                className={editInputCls}>
                                <option value="">—</option>
                                {uomOptions.map((u) => (
                                  <option key={u._id} value={u._id}>{u.Abbreviation || u.UnitOfMeasureName}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-1 py-1">
                              <input type="number" step="0.01" min="0"
                                value={editForm.quantity || ''}
                                onChange={(e) => handleLineChange('quantity', e.target.value)}
                                className={editInputCls + ' text-right'} />
                            </td>
                            <td className="px-1 py-1">
                              <input type="number" step="0.01" min="0"
                                value={editForm.freight || ''}
                                onChange={(e) => handleLineChange('freight', e.target.value)}
                                className={editInputCls + ' text-right'} />
                            </td>
                            <td className="px-1 py-1">
                              <input type="number" step="0.01"
                                value={editForm.adjustments ?? 0}
                                onChange={(e) => handleLineChange('adjustments', e.target.value)}
                                className={editInputCls + ' text-right'} />
                            </td>
                            <td className="px-1 py-1 text-right">
                              <span className="text-sm font-medium text-teal-700">
                                {PHP(computeTruckingSubTotal(editForm))}
                              </span>
                            </td>
                            <td className="px-1 py-1 text-center">
                              <button onClick={handleSaveLine} disabled={lineSaving}
                                className="p-1 text-teal-600 hover:text-teal-800 disabled:opacity-50">
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button onClick={handleCancelEdit}
                                className="p-1 text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ) : (
                          // ── Read-only row ───────────────────────────
                          <tr key={l._id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm">{l.Description || l.description}</td>
                            <td className="px-3 py-2 text-sm">
                              {resolveUOM(l, uomOptions)}
                            </td>
                            <td className="px-3 py-2 text-sm text-right">{l.Quantity ?? l.quantity}</td>
                            <td className="px-3 py-2 text-sm text-right">{PHP(l.Freight ?? l.freight)}</td>
                            <td className="px-3 py-2 text-sm text-right">{PHP(l.Adjustments ?? l.adjustments)}</td>
                            <td className="px-3 py-2 text-sm text-right font-medium">{PHP(l.SubTotal ?? l.subTotal)}</td>
                            <td className="px-3 py-2 text-center">
                              {!isVoided && (
                                <>
                                  <button onClick={() => startEdit(l)}
                                    className="p-1 text-gray-400 hover:text-teal-600">
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => handleDeleteLine(l._id)}
                                    disabled={!!deletingLineId}
                                    className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      )
                    )}

                    {/* ── Add new line row ─────────────────────────── */}
                    {editingId === 'new' && (
                      <tr className="bg-teal-50 ring-1 ring-teal-200 ring-inset">
                        <td className="px-1 py-1">
                          <input type="text" placeholder="Description" value={editForm.description || ''}
                            onChange={(e) => handleLineChange('description', e.target.value)}
                            className={editInputCls} />
                        </td>
                        <td className="px-1 py-1">
                          <select value={editForm.unitOfMeasureId || ''}
                            onChange={(e) => handleLineChange('unitOfMeasureId', e.target.value)}
                            className={editInputCls}>
                            <option value="">—</option>
                            {uomOptions.map((u) => (
                              <option key={u._id} value={u._id}>{u.Abbreviation || u.UnitOfMeasureName}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" step="0.01" min="0" placeholder="0"
                            value={editForm.quantity || ''}
                            onChange={(e) => handleLineChange('quantity', e.target.value)}
                            className={editInputCls + ' text-right'} />
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" step="0.01" min="0" placeholder="0"
                            value={editForm.freight || ''}
                            onChange={(e) => handleLineChange('freight', e.target.value)}
                            className={editInputCls + ' text-right'} />
                        </td>
                        <td className="px-1 py-1">
                          <input type="number" step="0.01" placeholder="0"
                            value={editForm.adjustments ?? 0}
                            onChange={(e) => handleLineChange('adjustments', e.target.value)}
                            className={editInputCls + ' text-right'} />
                        </td>
                        <td className="px-1 py-1 text-right">
                          <span className="text-sm font-medium text-teal-700">
                            {PHP(computeTruckingSubTotal(editForm))}
                          </span>
                        </td>
                        <td className="px-1 py-1 text-center">
                          <button onClick={handleSaveLine} disabled={lineSaving}
                            className="p-1 text-teal-600 hover:text-teal-800 disabled:opacity-50">
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button onClick={handleCancelEdit}
                            className="p-1 text-gray-400 hover:text-gray-600">
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Add Line button */}
                {!isVoided && (
                  <button
                    onClick={() => {
                      setEditingId('new');
                      setEditForm({ description: '', unitOfMeasureId: '', quantity: '', freight: '', adjustments: '0' });
                    }}
                    disabled={editingId !== null}
                    className="mt-2 flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 disabled:opacity-40"
                  >
                    <PlusCircleIcon className="h-4 w-4" />
                    Add Line
                  </button>
                )}

                {/* ── Summary Footer ─────────────────────────────── */}
                <div className="bg-gray-50 rounded-lg p-4 text-sm grid grid-cols-2 gap-2 max-w-xs ml-auto">
                  <SR label="Total Amount" value={`₱${PHP(totalAmount)}`} />
                  <SR label="Net of VAT"   value={`₱${PHP(netOfVat)}`} />
                  <SR label="VAT (12%)"    value={`₱${PHP(vat)}`} />
                  {stripFee > 0 && <SR label="Stripping Fee" value={`₱${PHP(stripFee)}`} />}
                  {addlStripFee > 0 && <SR label="Add'l Stripping Fee" value={`₱${PHP(addlStripFee)}`} />}
                  {form.HasWithHolding && <SR label="Withholding (2%)" value={`₱${PHP(withholding)}`} />}
                  <SR label="Amount Due" value={`₱${PHP(amountDue)}`} bold />
                </div>
              </div>
            )}

            {/* ── Action Buttons ────────────────────────────────── */}
            <div className="flex items-center gap-3 flex-wrap">
              <Btn primary onClick={handleSave} disabled={saving || isVoided} label={saving ? 'Saving…' : 'Save'} />
              {!isAdd && (
                <WaybillGLBadge
                  status={glPostingStatus}
                  error={glPostingError}
                  waybillId={id}
                  onRetry={handleRetryGL}
                  loading={glPosting}
                />
              )}
              {!isAdd && (
                <Btn
                  onClick={() => navigate(`/billing/trucking-billing/add?waybillId=${id}`)}
                  label="Create Trucking Billing"
                />
              )}
              {!isAdd && <Btn onClick={handlePrint} label="Print" />}
              {!isAdd && !isVoided && (
                <button
                  onClick={() => setShowVoidModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Void
                </button>
              )}
              <Btn onClick={() => navigate('/billing/trucking-waybill')} label="Back" />
            </div>
          </>
        )}
      </div>

      {/* ── Void Modal ─────────────────────────────────────────── */}
      {showVoidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
            <div className="flex items-center gap-3">
              <NoSymbolIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-base font-semibold text-gray-900">Void Waybill</h3>
            </div>
            <p className="text-sm text-gray-600">This action cannot be undone. The waybill will be marked as voided.</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Reason for voiding <span className="text-red-500">*</span></label>
              <textarea
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason…"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowVoidModal(false); setVoidReason(''); }} className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">Cancel</button>
              <button
                onClick={handleVoid}
                disabled={voiding || !voidReason.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {voiding ? 'Voiding…' : 'Confirm Void'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ── Helpers ──────────────────────────────────────────────────────
const editInputCls = 'w-full border-b border-teal-400 focus:outline-none text-sm py-0.5 px-1 bg-teal-50';

function F({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );
}
function NumericF({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={onChange}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );
}
function CB({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 text-teal-600 rounded" />
      {label}
    </label>
  );
}
function SR({ label, value, bold }) {
  return (
    <>
      <span className="text-gray-500">{label}</span>
      <span className={bold ? 'font-semibold text-gray-900 text-right' : 'text-gray-700 text-right'}>{value}</span>
    </>
  );
}
function Btn({ label, onClick, disabled, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
        primary ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
